import i18n from 'i18next';
import { put, call, takeLatest, all, select } from 'redux-saga/effects';
import { processRequest } from '../../services/Api';
import { historyPush, historyReplace } from '../../services/History';
import { listDetailsActionTypes } from './Constants';
import { getMadeLists, getSongs } from './Selectors';
import * as listsActions from '../lists/Actions';
import * as listDetailsActions from './Actions';
import * as notificationActions from '../../components/notification/Actions';
import * as errorHandlerActions from '../../components/errorHandler/Actions';

export default function* () {
	yield all([
		yield takeLatest(listDetailsActionTypes.GET_LIST_DETAILS_REQUEST, handleListDetailsRequest),
		yield takeLatest(listDetailsActionTypes.GET_LIST_RESULTS_REQUEST, handleListResultsRequest),
		yield takeLatest(listDetailsActionTypes.ADD_LIST_REQUEST, handleAddListRequest),
		yield takeLatest(listDetailsActionTypes.DUPLICATE_LIST_REQUEST, handleDuplicateListRequest),
		yield takeLatest(listDetailsActionTypes.UPDATE_LIST_REQUEST, handleUpdateListRequest),
		yield takeLatest(listDetailsActionTypes.DELETE_LIST_REQUEST, handleDeleteListRequest),
		yield takeLatest(listDetailsActionTypes.GET_LIST_SONGS_REQUEST, handleGetListSongsRequest),
		yield takeLatest(listDetailsActionTypes.DELETE_SONG_REQUEST, handleDeleteSongRequest),
	]);
}

export function* handleListDetailsRequest(action) {
	try {
		const { id } = action.payload;
		const { data } = yield call(processRequest, `public/lists/${id}`);

		yield put(listDetailsActions.getListDetailsSuccess(data));
	} catch (e) {
		if (e.response.status === 401) {
			yield put(listDetailsActions.setListStatus('private'));
		}
		if (e.response.status === 500 || e.response.status === 404) {
			yield put(errorHandlerActions.handleError(e));
		}
		yield put(listDetailsActions.getListDetailsError(e));
	}
}

export function* handleListResultsRequest(action) {
	const { id, fromResultsPage } = action.payload;

	try {
		const { data } = yield call(processRequest, 'results', 'GET', { list_id: id });

		yield put(listDetailsActions.getListResultsSuccess(data));
	} catch (e) {
		if (fromResultsPage) {
			yield put(errorHandlerActions.handleError(e));
		}
		yield put(listDetailsActions.getListResultsError());
	}
}

export function* handleAddListRequest(action) {
	try {
		const { type, id, list, itemType } = action.payload;
		const isQuizItem = itemType === 'user_quiz';
		const addTo = type === 'folders' ? 'folder' : 'group';
		const elementType = type.slice(0, -1);
		const notificationText = `${i18n.t(
			`list_details.notifications.add_${isQuizItem ? 'quiz' : 'list'}_to`,
			{
				context: elementType,
				count: list.length,
			},
		)}`;

		yield call(
			processRequest,
			isQuizItem
				? `user_quizzes/${id}/${addTo}_user_quizzes_collections`
				: `lists/${id}/${addTo}_lists_collections`,
			'POST',
			{
				[`${addTo}_ids`]: list,
			},
		);

		yield put(notificationActions.createNotification(notificationText));
		yield put(listDetailsActions.addListSuccess());
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'notification'));
		yield put(listDetailsActions.addListError());
	}
}

export function* handleDuplicateListRequest(action) {
	try {
		const { id, force } = action.payload;
		const { data } = yield call(processRequest, `lists/${id}/duplicate`, 'POST', { force });

		if (data.prompt) {
			yield put(
				listDetailsActions.toggleCopyListModal({
					open: true,
					sharedGroups: data.shared_groups,
					listId: id,
				}),
			);
		} else {
			yield call(historyPush, `/edit-lists/${data.id}`);
		}
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'notification'));
		yield put(listDetailsActions.duplicateListError(e));
	}
}

export function* handleUpdateListRequest(action) {
	try {
		const { id, listData } = action.payload;
		yield call(processRequest, `lists/${id}`, 'PATCH', { list: listData });
		yield put(listDetailsActions.updateListSuccess(listData));
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'notification'));
		yield put(listDetailsActions.updateListError(e));
	}
}

export function* handleDeleteListRequest(action) {
	try {
		const { id, isFromList, isUserQuiz, callback } = action.payload;
		yield call(processRequest, isUserQuiz ? `user_quizzes/${id}` : `lists/${id}`, 'DELETE');
		yield put(listDetailsActions.deleteListSuccess());

		if (callback) {
			callback();
			return;
		}

		if (isFromList) {
			const madeLists = yield select(getMadeLists);
			const updatedMadeLists = madeLists.filter((item) => item.id !== id);
			yield put(listsActions.getMadeListsSuccess(updatedMadeLists));
			return;
		}

		yield call(historyPush, '/recent');
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'notification'));
		yield put(listDetailsActions.deleteListError());
	}
}

export function* handleGetListSongsRequest({ payload }) {
	const { listId } = payload;

	try {
		const { data } = yield call(processRequest, `lists/${listId}/songs`);

		yield put(listDetailsActions.getListSongsSuccess(data.songs));
	} catch (e) {
		yield put(listDetailsActions.getListSongsError());
		yield put(errorHandlerActions.handleError(e));
	}
}

export function* handleDeleteSongRequest({ payload }) {
	const { listId, songId } = payload;

	try {
		const songs = yield select(getSongs);
		const filteredSongs = songs.filter((song) => song.id !== songId);

		yield call(processRequest, `donk_ai/${songId}/unfavorite_song`, 'DELETE', {
			list_id: listId,
		});

		if (!filteredSongs.length) yield call(historyReplace, `/lists/${listId}`);
		yield put(
			listDetailsActions.deleteSongSuccess({
				songs: filteredSongs,
				has_songs: filteredSongs.length,
			}),
		);
		yield put(
			notificationActions.createNotification(
				i18n.t('list_details.study_songs.confirm_modal.success_notification'),
			),
		);
	} catch (e) {
		yield put(listDetailsActions.deleteSongError());
		yield put(errorHandlerActions.handleError(e));
	}
}
